import { createAction, createAsyncThunk, createReducer } from '@reduxjs/toolkit';
import { API } from '../../constants';
import { application } from '../../services/application';
import { onPending, onReject } from '../helpers/sharedCases';
import {
  processEzInterpretResponse,
  stripChipFromCategoryFilters,
  buildScopesPayload,
  mergeSearchResultsWithLaneStatus,
} from './aiSearchHelpers';

export const INITIAL_STATE = {
  text: '',
  searchMode: 'keyword', // 'keyword' | 'ai'
  pending: false,
  error: null,
  results: {},
  aiState: {
    query: '',
    pending: false,
    error: null,
    complete: false,
    totalCount: 0,
    appliedFilters: [],
    originalFilters: [],
    explanations: {},
    categoryFilters: {},
    originalCategoryFilters: {},
    results: {},
    laneStatus: {},
  },
  recentSearches: [],
  filters: {
    order: ['createdAt', 'desc'],
    pagination: { page: 1, perPage: 5 },
  },
};

const changeTextAction = createAction('EZSearch/changeValue');
const setSearchModeAction = createAction('EZSearch/setSearchMode');

const fetchRecentSearchs = createAsyncThunk('EZSearch/getSavedSearchs', async (_, { getState }) => {
  const { filters } = getState().ezSearch;
  return application.call(API.EZ_SEARCH.GET_SAVED_SEARCHS, filters);
});

const fetchListAction = createAsyncThunk('EZSearch/fetchList', async (_, { getState }) => {
  const { text } = getState().ezSearch;
  return !text ? {} : application.call(API.EZ_SEARCH.GET_LIST, { text });
});

const fetchAiSearchAction = createAsyncThunk('EZSearch/fetchAiSearch', async (query, { dispatch }) => {
  const q = String(query || '').trim();
  if (!q) {
    return {
      query: '',
      totalCount: 0,
      appliedFilters: [],
      originalFilters: [],
      explanations: {},
      categoryFilters: {},
      originalCategoryFilters: {},
      results: {},
      laneStatus: {},
    };
  }

  // 1. Interpret via /v1/nlq/ez
  const interpretResponse = await application.call('naturalLanguage.interpretEz', { q });

  // 2. Map lanes, aggregate chips and explanations
  const { categoryFilters, explanations, laneStatus, appliedFilters } = processEzInterpretResponse(
    interpretResponse,
    q
  );

  // 3. Composite search with per-scope filters (unanswerable scopes omitted)
  const scopes = buildScopesPayload(categoryFilters, q);
  const searchResults = await application.call(API.EZ_SEARCH.GET_LIST, {
    text: q,
    scopes,
  });

  // 4. Save to recent searches
  dispatch(fetchRecentSearchs());

  // 5. Zero unanswerable scopes and total across the 8 EZ categories only
  const { results, totalCount } = mergeSearchResultsWithLaneStatus(searchResults, laneStatus);

  return {
    query: q,
    totalCount,
    appliedFilters,
    originalFilters: appliedFilters,
    explanations,
    categoryFilters,
    originalCategoryFilters: categoryFilters,
    results,
    laneStatus,
  };
});

const removeFilterChipAction = createAsyncThunk('EZSearch/removeFilterChip', async (chipId, { getState }) => {
  const { aiState } = getState().ezSearch;
  const chipToRemove = aiState.appliedFilters.find((c) => c.id === chipId);
  if (!chipToRemove) return null;

  const remainingChips = aiState.appliedFilters.filter((c) => c.id !== chipId);
  const updatedCategoryFilters = stripChipFromCategoryFilters(chipToRemove, aiState.categoryFilters);

  const scopes = buildScopesPayload(updatedCategoryFilters, aiState.query);
  const searchResults = await application.call(API.EZ_SEARCH.GET_LIST, {
    text: aiState.query,
    scopes,
  });

  const { results, totalCount } = mergeSearchResultsWithLaneStatus(searchResults, aiState.laneStatus);

  return {
    appliedFilters: remainingChips,
    categoryFilters: updatedCategoryFilters,
    results,
    totalCount,
  };
});

const resetFilterChipsAction = createAsyncThunk('EZSearch/resetFilterChips', async (_, { getState }) => {
  const { aiState } = getState().ezSearch;
  const scopes = buildScopesPayload(aiState.originalCategoryFilters, aiState.query);
  const searchResults = await application.call(API.EZ_SEARCH.GET_LIST, {
    text: aiState.query,
    scopes,
  });

  const { results, totalCount } = mergeSearchResultsWithLaneStatus(searchResults, aiState.laneStatus);

  return {
    appliedFilters: aiState.originalFilters,
    categoryFilters: aiState.originalCategoryFilters,
    results,
    totalCount,
  };
});

export const actions = {
  changeTextAction,
  setSearchModeAction,
  fetchListAction,
  fetchRecentSearchs,
  fetchAiSearchAction,
  removeFilterChipAction,
  resetFilterChipsAction,
};

const onFetchListFulfilled = (state, action) => {
  Object.assign(state, { results: action.payload, pending: false });
};

const onFetchSavedSearch = (state, action) => {
  Object.assign(state, { recentSearches: action.payload });
};

export default createReducer(INITIAL_STATE, (builder) => {
  builder
    .addCase(changeTextAction, (state, action) => {
      state.text = action.payload || '';
    })
    .addCase(setSearchModeAction, (state, action) => {
      state.searchMode = action.payload || 'keyword';
    })
    .addCase(fetchRecentSearchs.fulfilled, onFetchSavedSearch)
    .addCase(fetchListAction.fulfilled, onFetchListFulfilled)
    .addCase(fetchListAction.pending, onPending)
    .addCase(fetchListAction.rejected, onReject)

    // AI search
    .addCase(fetchAiSearchAction.pending, (state) => {
      state.aiState.pending = true;
      state.aiState.error = null;
    })
    .addCase(fetchAiSearchAction.fulfilled, (state, action) => {
      // Keep header input in sync for deep links / refresh
      if (action.payload?.query) {
        state.text = action.payload.query;
      }
      state.aiState = {
        ...state.aiState,
        ...action.payload,
        pending: false,
        complete: true,
        error: null,
      };
    })
    .addCase(fetchAiSearchAction.rejected, (state, action) => {
      state.aiState.pending = false;
      state.aiState.error = action.error?.message || 'Natural language search failed';
    })

    // Remove chip
    .addCase(removeFilterChipAction.pending, (state) => {
      state.aiState.pending = true;
    })
    .addCase(removeFilterChipAction.fulfilled, (state, action) => {
      if (action.payload) {
        state.aiState.appliedFilters = action.payload.appliedFilters;
        state.aiState.categoryFilters = action.payload.categoryFilters;
        state.aiState.results = action.payload.results;
        state.aiState.totalCount = action.payload.totalCount;
      }
      state.aiState.pending = false;
    })
    .addCase(removeFilterChipAction.rejected, (state) => {
      state.aiState.pending = false;
    })

    // Reset chips
    .addCase(resetFilterChipsAction.pending, (state) => {
      state.aiState.pending = true;
    })
    .addCase(resetFilterChipsAction.fulfilled, (state, action) => {
      state.aiState.appliedFilters = action.payload.appliedFilters;
      state.aiState.categoryFilters = action.payload.categoryFilters;
      state.aiState.results = action.payload.results;
      state.aiState.totalCount = action.payload.totalCount;
      state.aiState.pending = false;
    })
    .addCase(resetFilterChipsAction.rejected, (state) => {
      state.aiState.pending = false;
    });
});
